import { memo } from 'react';

import { cn } from '@infinex/ui-styles';

import { useCustodyDisplayItems } from '../../api';
import type { WalletView } from '../../hooks';
import { useWalletView } from '../../hooks';
import { WalletLabelPill } from '../WalletLabelPill';

interface AssetListSectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
  titleSize?: 'base' | 'small';
  /**
   * If provided, use this wallet view instead of useWalletView hook
   * For example, in move when the user selects a wallet, it'll not update the url params, so use this prop instead.
   */
  walletView?: WalletView;
  /** If true, shows the custody view pill based on current wallet view */
  showCustodyViewPill?: boolean;
  /** Optional element to render inline with the section title */
  titleAdornment?: React.ReactNode;
  hideSectionTitle?: boolean;
  /** If false, the title scrolls with the list instead of pinning on top */
  stickyTitle?: boolean;
}

/**
 * The custody-view pill, in its own component so its data hooks (wallet-view
 * search params, custody display items and their portfolio contexts) run ONLY
 * when a caller actually shows the pill — hosts without those providers
 * (the confidential app) render the sections pill-less without crashing.
 */
function CustodyViewPill({
  walletView: providedWalletView,
}: {
  walletView?: WalletView;
}) {
  const { currentWalletView } = useWalletView({
    defaultToMainWalletIfNothingSelected: true,
  });

  const custodyDisplayItems = useCustodyDisplayItems({
    includeTotalPortfolio: true,
    includeVaults: true,
  });

  // Find the matching custody item for the current wallet view
  const currentCustodyItem = custodyDisplayItems.value.walletItems.find(
    (item) => item.id === (providedWalletView || currentWalletView)
  );

  if (!currentCustodyItem) return null;
  return (
    <WalletLabelPill
      label={currentCustodyItem.label}
      icon={currentCustodyItem.icon}
    />
  );
}

export const AssetListSection = memo<AssetListSectionProps>(
  ({
    title,
    children,
    className,
    titleSize = 'base',
    showCustodyViewPill,
    walletView: providedWalletView,
    titleAdornment,
    hideSectionTitle,
    stickyTitle = true,
  }: AssetListSectionProps) => {
    if (hideSectionTitle) {
      // Keep the same list container as the titled render so rows stay 8px
      // apart instead of picking up the parent's section gap.
      return (
        <div className={cn('flex flex-col gap-y-2', className)}>{children}</div>
      );
    }

    return (
      <div className={cn('flex flex-col gap-y-3', className)}>
        {/* Sticky title on scroll (unless opted out). The horizontal inset is
            part of the sticky-bar chrome; plain titles sit flush. */}
        <div
          className={cn(
            '-mb-2 py-2',
            stickyTitle && 'bg-canvas sticky top-0 z-10 px-4'
          )}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <h3
              className={cn(
                'font-semibold whitespace-nowrap',
                titleSize === 'small' ? 'text-sm' : 'text-emphasis text-base'
              )}
            >
              {title}
            </h3>

            {showCustodyViewPill && (
              <CustodyViewPill walletView={providedWalletView} />
            )}

            {titleAdornment}
          </div>
        </div>

        {/* List container */}
        <div className="flex flex-col gap-y-2">{children}</div>
      </div>
    );
  }
);
